// One shared wind field for everything on the client that moves in the breeze: trees
// (frozenTrees.ts), live growth tips and the grass (grass.ts).
//
// The wind is owned by the simulation: it is a deterministic function of position and time
// (wind.py is the same function), so the server, the particle physics and every viewer agree on
// it without exchanging anything but the clock. The server sends that clock and the parameters
// (a "wind" message, about four times a second); this class advances the clock between messages
// from the measured tick rate and eases toward the server's value, and the shaders evaluate the
// field from it. Until the first message arrives, or when the server sends none, it runs on its
// own wall-clock time as it always did. The quadruped does not perceive the wind.
//
// Keep WIND_GLSL, WindField.update and wind.py identical in their formulas.
//
// The field is a function of position and time evaluated in the vertex shaders, so it costs
// no CPU per tree or per blade. Gusts are bands that travel along the wind direction at a
// few metres per second, with their fronts wobbled sideways so they are not straight lines.
// Two sinusoids of different wavelength give irregular spacing without any noise texture.
// Everything that samples it sees the same gust at the same place and moment, so a gust
// visibly crosses the grass, then reaches the trees.
//
// Units: three-space metres (x, z horizontal), seconds. The wind push returned by
// windPush() is a dimensionless 0..~1 strength along uWindDir.
import * as THREE from 'three';

export const WIND_GLSL = /* glsl */`
uniform float uWindT;
uniform vec2  uWindDir;
uniform float uWindAmp;

// Gust band strength 0..1 at a ground position.
float windGust(vec2 xz) {
  vec2 n = vec2(-uWindDir.y, uWindDir.x);
  float s = dot(xz, uWindDir);
  float c = dot(xz, n);
  s += 7.0 * sin(c * 0.045 + 0.4);                                   // fronts are not straight
  float g1 = sin(6.2831853 * (s / 41.0 - uWindT * 0.17));           // ~7.0 m/s
  float g2 = sin(6.2831853 * (s / 17.0 - uWindT * 0.40) + 1.7 + c * 0.03);  // ~6.8 m/s
  float g = clamp(0.5 + 0.38 * g1 + 0.22 * g2, 0.0, 1.0);
  return g * g * (3.0 - 2.0 * g);
}

// Horizontal push along the wind: a steady breeze plus the gusts.
vec2 windPush(vec2 xz) {
  return uWindDir * (uWindAmp * (0.30 + 0.70 * windGust(xz)));
}

// Displacement of a point on a tree, given the wind push to use. info = (base y, top y) of that
// tree; bending grows with the square of the height fraction, so the trunk base stays planted
// and the crown moves. Callers that move many points of one tree (wood endpoints, needle
// anchors) evaluate windPush() once at the trunk and pass it in: gusts are 17 m and longer, so
// the trunk's value is the crown's value for any tree, and every piece of a tree moves on the
// same push, which keeps joints closed and saves the gust sines per vertex.
vec3 treeSwayAt(vec3 p, vec2 info, float slot, vec2 push) {
  float H = info.y - info.x;
  if (H < 0.3) return vec3(0.0);
  float f = clamp((p.y - info.x) / H, 0.0, 1.2);
  float ph = fract(slot * 0.61803) * 6.2831853;
  float sq = sqrt(max(H, 1.0));
  float osc  = sin(uWindT * 6.2831853 * (0.85 / sq) + ph);          // each tree has its own rhythm,
  float osc2 = sin(uWindT * 6.2831853 * (1.30 / sq) + ph * 1.7);    // taller trees sway slower
  vec2 perp = vec2(-uWindDir.y, uWindDir.x);
  vec2 d = push * (1.0 + 0.30 * osc) + perp * (0.15 * osc2 * length(push));
  float bend = f * f * H * 0.035;
  return vec3(d.x, 0.0, d.y) * bend;
}

// Same, sampling the wind at the point itself.
vec3 treeSway(vec3 p, vec2 info, float slot) {
  return treeSwayAt(p, info, slot, windPush(p.xz));
}
`;

/** The server's "wind" message (see broadcast_server.py). */
export interface WindMessage {
  type: 'wind';
  tick: number;
  t: number;               // wind clock, seconds, at `tick`
  secs_per_tick: number;   // wind seconds per simulation tick
  base_angle: number;      // mean direction, radians, three x-z plane
  strength: number;        // overall multiplier on the push
  storm?: number;          // extra multiplier while a storm blows (1 = calm)
  speed_ms?: number;       // wind speed in m/s at a push of 1 (used by the simulation, not the shaders)
}

// A server clock further than this many wind seconds from ours is a pause, a restart or a long
// stall rather than drift: snap to it instead of easing.
const SNAP_S = 5;
// How long the ease toward the server's clock takes, seconds of wall time.
const EASE_S = 0.5;
// Storms rise and fall over minutes; this only smooths the step between messages.
const STORM_EASE_S = 1.0;

export class WindField {
  readonly uniforms = {
    uWindT:   { value: 0 },
    uWindDir: { value: new THREE.Vector2(0.8, -0.6) },
    uWindAmp: { value: 1 },
  };
  /** Overall strength multiplier (1 = a steady breeze with clear gusts; 0 = still air). */
  strength = 1;
  /** Mean wind direction, radians, in the three x-z plane. */
  baseAngle = -0.64;
  private time = 0;
  private serverDriven = false;
  private secsPerTick = 0;
  private correction = 0;   // wind seconds still to ease in toward the server's clock
  private storm = 1;         // storm multiplier on the push, eased toward the server's value
  private stormTarget = 1;

  /** Follow the server's wind clock (a "wind" message). */
  applyServer(msg: WindMessage) {
    this.strength = msg.strength;
    this.baseAngle = msg.base_angle;
    this.stormTarget = msg.storm ?? 1;
    if (!this.serverDriven) this.storm = this.stormTarget;
    this.secsPerTick = msg.secs_per_tick;
    const err = msg.t - this.time;
    if (!this.serverDriven || Math.abs(err) > SNAP_S) {
      this.time = msg.t;
      this.correction = 0;
    } else {
      this.correction = err;
    }
    this.serverDriven = true;
  }

  /**
   * Advance by `dt` wall seconds. `simTps` is the measured simulation tick rate; once the server
   * is driving the clock it advances at that many ticks per second times the wind seconds per
   * tick, so a paused or slowed simulation pauses or slows its wind too.
   */
  update(dt: number, simTps = 0) {
    const d = Math.max(0, Math.min(dt, 0.25));
    if (this.serverDriven) {
      this.time += d * simTps * this.secsPerTick;
      const k = Math.min(1, d / EASE_S);
      const step = this.correction * k;
      this.time += step;
      this.correction -= step;
    } else {
      this.time += d;
    }
    this.storm += (this.stormTarget - this.storm) * Math.min(1, d / STORM_EASE_S);
    const t = this.time;
    // the direction drifts a few tens of degrees over minutes; the strength breathes slowly
    const a = this.baseAngle + 0.35 * Math.sin(t * 0.031) + 0.15 * Math.sin(t * 0.083 + 1.3);
    this.uniforms.uWindDir.value.set(Math.cos(a), Math.sin(a));
    this.uniforms.uWindAmp.value = this.strength * (0.85 + 0.15 * Math.sin(t * 0.07 + 0.5)) * this.storm;
    this.uniforms.uWindT.value = t;
  }
}
